import React, { Component } from 'react'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import { Card, CardHeader, CardBody, Table, Badge, Input, FormGroup, Label, Row, Col } from 'reactstrap'
import { Scatter } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'

const QUERY = gql`
  query StatsWelderBenchmark {
    stats_welder_benchmark {
      person_id
      welder_name
      welds_count
      total_mm
      active_days
      wps_count
      mm_per_day
      total_inspected
      total_failed
      defect_rate
      vs_avg_productivity
      vs_avg_defect_rate
    }
  }
`

const vsColor = (val, inverse = false) => {
  if (val === null || val === undefined) return 'secondary'
  const positive = inverse ? val < 0 : val > 0
  return positive ? 'success' : val === 0 ? 'secondary' : 'danger'
}

class WelderBenchmark extends Component {
  constructor(props) {
    super(props)
    this.state = { sortBy: 'total_mm', sortDir: 'desc', search: '' }
  }

  sorted(data) {
    const { sortBy, sortDir, search } = this.state
    let filtered = search ? data.filter(w => w.welder_name.toLowerCase().includes(search.toLowerCase())) : data
    return [...filtered].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy]
      if (typeof av === 'string') return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv)
      return sortDir === 'desc' ? (bv || 0) - (av || 0) : (av || 0) - (bv || 0)
    })
  }

  toggleSort(col) {
    this.setState(s => ({ sortBy: col, sortDir: s.sortBy === col && s.sortDir === 'desc' ? 'asc' : 'desc' }))
  }

  render() {
    return (
      <Query query={QUERY} fetchPolicy="network-only">
        {({ loading, error, data }) => (
          <div className="animated fadeIn">
            <Card>
              <CardHeader><strong><i className="fa fa-users mr-2" />Welder Benchmarking</strong></CardHeader>
              <CardBody style={{ position: 'relative', minHeight: '200px' }}>
                {loading && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
                {error && <div className="text-danger">Error: {error.message}</div>}
                {!loading && !error && data && data.stats_welder_benchmark && (() => {
                  const welders = this.sorted(data.stats_welder_benchmark)
                  const scatterData = {
                    datasets: [{
                      label: 'Welders',
                      data: data.stats_welder_benchmark.map(w => ({ x: w.mm_per_day, y: w.defect_rate, label: w.welder_name })),
                      backgroundColor: 'rgba(54,162,235,0.6)',
                      pointRadius: 6
                    }]
                  }

                  return (
                    <div>
                      <Row className="mb-3">
                        <Col md="12">
                          <h6>Productivity vs Defect Rate</h6>
                          <div style={{ height: '250px' }}>
                            <Scatter data={scatterData} options={{
                              maintainAspectRatio: false,
                              tooltips: {
                                callbacks: {
                                  label: (item, d) => {
                                    const pt = d.datasets[item.datasetIndex].data[item.index]
                                    return `${pt.label}: ${pt.x} mm/day, ${pt.y}% defects`
                                  }
                                }
                              },
                              scales: {
                                xAxes: [{ scaleLabel: { display: true, labelString: 'mm / active day' } }],
                                yAxes: [{ scaleLabel: { display: true, labelString: 'Defect rate %' }, ticks: { beginAtZero: true } }]
                              }
                            }} />
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="4">
                          <FormGroup>
                            <Label>Search welder</Label>
                            <Input placeholder="Name..." value={this.state.search}
                              onChange={e => this.setState({ search: e.target.value })} />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Table bordered striped responsive size="sm">
                        <thead>
                          <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => this.toggleSort('welder_name')}>
                                Welder {this.state.sortBy === 'welder_name' ? (this.state.sortDir === 'desc' ? '▼' : '▲') : ''}
                              </th>
                            {[['total_mm','Total mm'],['mm_per_day','mm/day'],['welds_count','Welds'],['active_days','Active days'],['wps_count','WPS used'],['defect_rate','Defect %'],['vs_avg_productivity','vs avg prod.'],['vs_avg_defect_rate','vs avg defect']].map(([col, label]) => (
                              <th key={col} style={{ cursor: 'pointer' }} onClick={() => this.toggleSort(col)}>
                                {label} {this.state.sortBy === col ? (this.state.sortDir === 'desc' ? '▼' : '▲') : ''}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {welders.map(w => (
                            <tr key={w.person_id}>
                              <td><strong>{w.welder_name}</strong></td>
                              <td>{w.total_mm.toLocaleString()}</td>
                              <td>{w.mm_per_day.toLocaleString()}</td>
                              <td>{w.welds_count}</td>
                              <td>{w.active_days}</td>
                              <td>{w.wps_count}</td>
                              <td style={{ color: w.defect_rate > 2 ? 'red' : w.defect_rate > 0 ? 'orange' : 'inherit' }}>
                                {w.defect_rate > 0 ? `${w.defect_rate}%` : '—'}
                              </td>
                              <td>
                                <Badge color={vsColor(w.vs_avg_productivity)}>
                                  {w.vs_avg_productivity > 0 ? '+' : ''}{w.vs_avg_productivity}%
                                </Badge>
                              </td>
                              <td>
                                <Badge color={vsColor(w.vs_avg_defect_rate, true)}>
                                  {w.vs_avg_defect_rate > 0 ? '+' : ''}{w.vs_avg_defect_rate}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div className="mt-3 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                        <strong>How it's calculated:</strong>
                        <ul className="mb-0 mt-1 pl-3">
                          <li><strong>Total mm</strong> — sum of all completed welding logs [mm]</li>
                          <li><strong>mm/day</strong> — Total mm / number of distinct days with welding activity</li>
                          <li><strong>Welds</strong> — number of distinct welds the welder worked on</li>
                          <li><strong>Active days</strong> — number of distinct calendar days with at least one welding log</li>
                          <li><strong>WPS used</strong> — number of distinct WPS procedures used across all welds</li>
                          <li><strong>Defect %</strong> — <em>failed mm / inspected mm × 100</em>; inspection is attributed to the welder of the same weld (RI excluded)</li>
                          <li><strong>vs avg prod.</strong> — welder mm/day vs average mm/day across all welders: <em>(welder − avg) / avg × 100%</em></li>
                          <li><strong>vs avg defect</strong> — welder defect rate minus average defect rate (negative = better than average)</li>
                        </ul>
                      </div>
                    </div>
                  )
                })()}
              </CardBody>
            </Card>
          </div>
        )}
      </Query>
    )
  }
}

export default WelderBenchmark



// WEBPACK FOOTER //
// ./src/components/Report/WelderBenchmark.js